/**
 * Self-test suite for per-attachment captions.
 *
 * Mirrors `kaether_for_ios/Stoat/E2EE/AttachmentCaptionSelfTest.swift`
 * case-for-case across four groups:
 *
 *   A. Codec (~9): round-trip; isCaption prefix non-collision; tryDecode returns
 *      null for non-captions; empty-text round-trip; exactly 1500 scalars
 *      allowed; 1501 scalars rejected on encode; emoji caption with
 *      unicodeScalars.count > 1500 rejected; empty attachmentId rejected;
 *      unsupported schema rejected.
 *
 *   B. Store (~8): apply→read round-trip; newer-ts wins; older-ts rejected;
 *      equal-ts rejected (no observedAt churn); empty text overwrites
 *      (allowed "edit to clear"); clear removes; resetAll empties; persistence
 *      across store instances.
 *
 *   C. setLocal convenience (~4): valid payload round-trips through codec;
 *      oversize → throws captionTooLong; empty attachmentId → throws;
 *      setLocal updates the store as a side effect.
 *
 *   D. Server-rebind resistance (~3): hostile payload routes to its own
 *      attachmentId (not the receiver's expected one); foreign-ratchet caption
 *      still passes codec (envelope authenticates, not codec); apply has no
 *      external expectedAttachmentId parameter.
 *
 * All cases use isolated IndexedDB database names so the suite is safe to run
 * on every dev launch without polluting production state.
 *
 * Registered with the global SelfTestRunner singleton at module load time.
 * The runner auto-discovers this file via Vite `import.meta.glob` in
 * `selftest/index.ts` (matches `**\/*SelfTest.ts`).
 */

import { SelfTestRunner, buildSuiteResult, fail, pass } from "../selftest";
import type { TestCase } from "../selftest";
import {
  AttachmentCaptionCodec,
  AttachmentCaptionError,
  CAPTION_SCHEMA_VERSION,
  countUnicodeScalars,
  makeAttachmentCaptionPayload,
} from "./AttachmentCaption";
import { AttachmentCaptionStore } from "./AttachmentCaptionStore";

// ---------------------------------------------------------------------------
// Fixture helpers
// ---------------------------------------------------------------------------

let dbCounter = 0;

/** Returns a fresh, isolated AttachmentCaptionStore backed by a unique IDB database. */
function freshStore(): AttachmentCaptionStore {
  dbCounter += 1;
  return new AttachmentCaptionStore(
    `kaether:attachment-captions:selftest:${dbCounter}:${Date.now()}`,
  );
}

/** base64url-encode a UTF-8 string — used to craft synthetic wire payloads in tests. */
function b64url(s: string): string {
  const bytes = new TextEncoder().encode(s);
  let binary = "";
  for (let i = 0; i < bytes.length; i++) {
    binary += String.fromCharCode(bytes[i]!);
  }
  return btoa(binary).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
}

// ---------------------------------------------------------------------------
// Suite registration
// ---------------------------------------------------------------------------

SelfTestRunner.register({
  name: "Attachment captions (per-media text)",
  isVerified: true,
  run: async () =>
    buildSuiteResult(
      await Promise.all([
        // A. Codec
        testCodecRoundTrip(),
        testIsCaptionDetection(),
        testTryDecodeReturnsNullForNonCaption(),
        testEmptyTextRoundTrip(),
        testExactly1500ScalarsAllowed(),
        testCaptionTooLongRejectedOnEncode(),
        testDecodeRejectsOversizeCrafted(),
        testEmojiCaptionScalarCountRejected(),
        testEmptyAttachmentIdRejected(),
        testUnsupportedSchemaRejected(),

        // B. Store
        testApplyThenCaptionRoundTrip(),
        testNewerTsWins(),
        testOlderTsRejected(),
        testEqualTsRejected(),
        testEmptyTextOverwritesExisting(),
        testClearRemovesRecord(),
        testResetAllEmpties(),
        testPersistenceAcrossInstances(),

        // C. setLocal convenience
        testSetLocalRoundTripsViaCodec(),
        testSetLocalOversizeThrows(),
        testSetLocalEmptyAttachmentIdThrows(),
        testSetLocalUpdatesStoreSideEffect(),

        // D. Server-rebind resistance
        testHostilePayloadRoutesByPayloadAttachmentId(),
        testForeignRatchetCaptionStillPassesCodec(),
        testApplyHasNoExpectedAttachmentIdParam(),
      ]),
    ),
});

// ---------------------------------------------------------------------------
// A. Codec cases
// ---------------------------------------------------------------------------

async function testCodecRoundTrip(): Promise<TestCase> {
  const name = "codec round-trips caption (attachmentId + captionText + ts preserved)";
  try {
    const p = makeAttachmentCaptionPayload("att-1", "look at this", 1_700_000_000);
    const encoded = AttachmentCaptionCodec.encode(p);
    const decoded = AttachmentCaptionCodec.decode(encoded);
    if (
      decoded.schema === p.schema &&
      decoded.attachmentId === p.attachmentId &&
      decoded.captionText === p.captionText &&
      decoded.ts === p.ts
    ) {
      return pass(name);
    }
    return fail(
      name,
      `mismatch — got ${JSON.stringify(decoded)}, want ${JSON.stringify(p)}`,
    );
  } catch (e) {
    return fail(name, String(e));
  }
}

async function testIsCaptionDetection(): Promise<TestCase> {
  const name =
    "isCaption distinguishes from chat/envelope/receipt/tombstone/reply/pin/draft/skdm/skmsg/role/gmember/voip-call/call:link/voice/voipush";
  try {
    const p = makeAttachmentCaptionPayload("a", "hi", 1);
    const encoded = AttachmentCaptionCodec.encode(p);
    const positive = AttachmentCaptionCodec.isCaption(encoded);
    // Every other kaether control-message prefix must NOT be mistaken for a caption.
    const negChat      = AttachmentCaptionCodec.isCaption("hello world");
    const negEnvelope  = AttachmentCaptionCodec.isCaption("kaether:e2ee:v1:foo");
    const negReceipt   = AttachmentCaptionCodec.isCaption("kaether:receipt:v1:abc");
    const negTombstone = AttachmentCaptionCodec.isCaption("kaether:tombstone:v1:abc");
    const negReply     = AttachmentCaptionCodec.isCaption("kaether:reply:v1:abc");
    const negPin       = AttachmentCaptionCodec.isCaption("kaether:pin:v1:abc");
    const negDraft     = AttachmentCaptionCodec.isCaption("kaether:draft:v1:abc");
    const negSkdm      = AttachmentCaptionCodec.isCaption("kaether:skdm:v1:abc");
    const negSkmsg     = AttachmentCaptionCodec.isCaption("kaether:skmsg:v1:abc");
    const negRole      = AttachmentCaptionCodec.isCaption("kaether:role:v1:abc");
    const negGmember   = AttachmentCaptionCodec.isCaption("kaether:gmember:v1:abc");
    const negVoipCall  = AttachmentCaptionCodec.isCaption("kaether:voip-call:v1:abc");
    const negCallLink  = AttachmentCaptionCodec.isCaption("kaether:call:link:v1:abc");
    const negVoice     = AttachmentCaptionCodec.isCaption("kaether:voice:v1:abc");
    const negVoipush   = AttachmentCaptionCodec.isCaption("kaether:voipush:v1:abc");
    if (
      positive &&
      !negChat && !negEnvelope && !negReceipt && !negTombstone &&
      !negReply && !negPin && !negDraft && !negSkdm && !negSkmsg &&
      !negRole && !negGmember && !negVoipCall && !negCallLink &&
      !negVoice && !negVoipush
    ) {
      return pass(name);
    }
    return fail(
      name,
      `pos=${positive} chat=${negChat} env=${negEnvelope} rcpt=${negReceipt} tomb=${negTombstone} reply=${negReply} pin=${negPin} draft=${negDraft} skdm=${negSkdm} skmsg=${negSkmsg} role=${negRole} gmember=${negGmember} voip-call=${negVoipCall} call:link=${negCallLink} voice=${negVoice} voipush=${negVoipush}`,
    );
  } catch (e) {
    return fail(name, String(e));
  }
}

async function testTryDecodeReturnsNullForNonCaption(): Promise<TestCase> {
  const name = "tryDecode returns null for non-caption strings";
  try {
    const r = AttachmentCaptionCodec.tryDecode("just a chat message");
    if (r === null) return pass(name);
    return fail(name, `expected null, got ${JSON.stringify(r)}`);
  } catch (e) {
    return fail(name, `threw unexpectedly: ${e}`);
  }
}

async function testEmptyTextRoundTrip(): Promise<TestCase> {
  const name = "codec round-trips empty-text caption (explicit edit-to-clear)";
  try {
    const p = makeAttachmentCaptionPayload("att-1", "", 1_700_000_001);
    const encoded = AttachmentCaptionCodec.encode(p);
    const decoded = AttachmentCaptionCodec.decode(encoded);
    if (
      decoded.captionText === "" &&
      decoded.attachmentId === "att-1" &&
      decoded.ts === 1_700_000_001
    ) {
      return pass(name);
    }
    return fail(name, `mismatch — got ${JSON.stringify(decoded)}`);
  } catch (e) {
    return fail(name, String(e));
  }
}

async function testExactly1500ScalarsAllowed(): Promise<TestCase> {
  const name = "exactly 1500 unicode scalars is allowed on encode and decode";
  try {
    const text = "a".repeat(AttachmentCaptionCodec.MAX_UNICODE_SCALAR_COUNT);
    const p = makeAttachmentCaptionPayload("att-1", text, 1);
    const encoded = AttachmentCaptionCodec.encode(p);
    const decoded = AttachmentCaptionCodec.decode(encoded);
    const count = countUnicodeScalars(decoded.captionText);
    if (count === AttachmentCaptionCodec.MAX_UNICODE_SCALAR_COUNT) {
      return pass(name);
    }
    return fail(name, `got count=${count}, want ${AttachmentCaptionCodec.MAX_UNICODE_SCALAR_COUNT}`);
  } catch (e) {
    return fail(name, String(e));
  }
}

async function testCaptionTooLongRejectedOnEncode(): Promise<TestCase> {
  const name = "1501 unicode scalars is rejected with captionTooLong on encode";
  try {
    const text = "a".repeat(AttachmentCaptionCodec.MAX_UNICODE_SCALAR_COUNT + 1);
    const p = makeAttachmentCaptionPayload("att-1", text, 1);
    AttachmentCaptionCodec.encode(p);
    return fail(name, "encode did not throw");
  } catch (e) {
    if (
      e instanceof AttachmentCaptionError &&
      e.kind.tag === "captionTooLong" &&
      e.kind.unicodeScalarCount === AttachmentCaptionCodec.MAX_UNICODE_SCALAR_COUNT + 1
    ) {
      return pass(name);
    }
    return fail(name, `wrong error: ${e}`);
  }
}

async function testDecodeRejectsOversizeCrafted(): Promise<TestCase> {
  const name =
    "decode rejects maliciously-crafted ciphertext yielding >1500 unicode scalars";
  // Craft a raw JSON with oversize captionText and inject it directly as
  // base64url, bypassing encode (which would reject it first). This simulates
  // a hostile server that crafts the ciphertext itself.
  const oversizeText = "a".repeat(AttachmentCaptionCodec.MAX_UNICODE_SCALAR_COUNT + 1);
  const raw = JSON.stringify({
    schema: CAPTION_SCHEMA_VERSION,
    attachmentId: "att-1",
    captionText: oversizeText,
    ts: 1,
  });
  const crafted = AttachmentCaptionCodec.PREFIX + b64url(raw);
  try {
    AttachmentCaptionCodec.decode(crafted);
    return fail(name, "decode did not throw");
  } catch (e) {
    if (
      e instanceof AttachmentCaptionError &&
      e.kind.tag === "captionTooLong"
    ) {
      return pass(name);
    }
    return fail(name, `wrong error: ${e}`);
  }
}

async function testEmojiCaptionScalarCountRejected(): Promise<TestCase> {
  const name =
    "emoji caption with unicodeScalars.count > 1500 is rejected even when str.length is small";
  // 🤦‍♂️ is U+1F926 U+200D U+2642 U+FE0F — 4 code points, but str.length === 5
  // (two surrogates for U+1F926, ZWJ, ♂ U+2642, VS16 U+FE0F).
  // 1499 × 4 = 5996 scalars > 1500, but str.length ≈ 7495 which is still
  // much larger — the key is that a grapheme-cluster-based ".count == 1499"
  // (one grapheme cluster each) would pass a naive 1500-char cap while
  // this emoji actually has 4 scalars per cluster.
  // We use the family emoji 👨‍👩‍👧 (5 scalars: man + ZWJ + woman + ZWJ + girl)
  // × 301 = 1505 scalars > 1500, but grapheme .count === 301 (well under 1500).
  const family = "👨‍👩‍👧"; // 5 unicode scalars, 1 grapheme cluster
  const scalarCount = countUnicodeScalars(family);
  if (scalarCount !== 5) {
    return fail(
      name,
      `fixture check failed: expected 5 scalars for 👨‍👩‍👧, got ${scalarCount}`,
    );
  }
  const oversizedByScalars = family.repeat(301); // 301 × 5 = 1505 scalars
  const totalScalars = countUnicodeScalars(oversizedByScalars);
  // Grapheme-cluster count (str.length-equivalent via [...]) is 301 — well under 1500
  const graphemeClusters = [...oversizedByScalars].length; // same as countUnicodeScalars here since each char is counted per spread
  // Actually, [...family] counts 5 items per family emoji since they're individual code points.
  // The grapheme cluster (user-perceived character) count would be 301.
  // Let's verify totalScalars > 1500 and that a naive "String.prototype.length / 2 approx" cap would fail.
  if (totalScalars <= AttachmentCaptionCodec.MAX_UNICODE_SCALAR_COUNT) {
    return fail(
      name,
      `fixture invalid: 301 × family emoji should be ${301 * 5}=1505 scalars > 1500, got ${totalScalars}`,
    );
  }
  const p = makeAttachmentCaptionPayload("att-1", oversizedByScalars, 1);
  try {
    AttachmentCaptionCodec.encode(p);
    return fail(
      name,
      `encode accepted emoji-heavy payload (scalars=${totalScalars})`,
    );
  } catch (e) {
    if (
      e instanceof AttachmentCaptionError &&
      e.kind.tag === "captionTooLong" &&
      e.kind.unicodeScalarCount === totalScalars
    ) {
      return pass(name);
    }
    return fail(name, `wrong error: ${e}`);
  }
}

async function testEmptyAttachmentIdRejected(): Promise<TestCase> {
  const name = "empty attachmentId is rejected on encode with emptyAttachmentId";
  try {
    const p = makeAttachmentCaptionPayload("", "hi", 1);
    AttachmentCaptionCodec.encode(p);
    return fail(name, "encode did not throw");
  } catch (e) {
    if (
      e instanceof AttachmentCaptionError &&
      e.kind.tag === "emptyAttachmentId"
    ) {
      return pass(name);
    }
    return fail(name, `wrong error: ${e}`);
  }
}

async function testUnsupportedSchemaRejected(): Promise<TestCase> {
  const name = "unsupported schema version is rejected on decode";
  const json = JSON.stringify({
    schema: 99,
    attachmentId: "a",
    captionText: "hi",
    ts: 1,
  });
  const crafted = AttachmentCaptionCodec.PREFIX + b64url(json);
  try {
    AttachmentCaptionCodec.decode(crafted);
    return fail(name, "decode did not throw");
  } catch (e) {
    if (
      e instanceof AttachmentCaptionError &&
      e.kind.tag === "unsupportedSchema" &&
      e.kind.version === 99
    ) {
      return pass(name);
    }
    return fail(name, `wrong error: ${e}`);
  }
}

// ---------------------------------------------------------------------------
// B. Store cases
// ---------------------------------------------------------------------------

async function testApplyThenCaptionRoundTrip(): Promise<TestCase> {
  const name = "apply then caption(attachmentId) returns the same content";
  try {
    const s = freshStore();
    const p = makeAttachmentCaptionPayload("att-1", "hello", 100);
    const changed = await s.apply(p);
    const r = await s.caption("att-1");
    if (
      changed &&
      r?.captionText === "hello" &&
      r.lastTs === 100 &&
      r.attachmentId === "att-1"
    ) {
      return pass(name);
    }
    return fail(name, `changed=${changed} got=${JSON.stringify(r)}`);
  } catch (e) {
    return fail(name, String(e));
  }
}

async function testNewerTsWins(): Promise<TestCase> {
  const name = "newer ts overwrites older caption";
  try {
    const s = freshStore();
    await s.apply(makeAttachmentCaptionPayload("att-1", "old", 100));
    const changed = await s.apply(makeAttachmentCaptionPayload("att-1", "new", 200));
    const r = await s.caption("att-1");
    if (changed && r?.captionText === "new" && r.lastTs === 200) {
      return pass(name);
    }
    return fail(name, `changed=${changed} got=${JSON.stringify(r)}`);
  } catch (e) {
    return fail(name, String(e));
  }
}

async function testOlderTsRejected(): Promise<TestCase> {
  const name = "older ts is rejected (no resurrect)";
  try {
    const s = freshStore();
    await s.apply(makeAttachmentCaptionPayload("att-1", "current", 200));
    const changed = await s.apply(makeAttachmentCaptionPayload("att-1", "stale", 100));
    const r = await s.caption("att-1");
    if (!changed && r?.captionText === "current" && r.lastTs === 200) {
      return pass(name);
    }
    return fail(name, `changed=${changed} got=${JSON.stringify(r)}`);
  } catch (e) {
    return fail(name, String(e));
  }
}

async function testEqualTsRejected(): Promise<TestCase> {
  const name = "equal ts is rejected (idempotent, no observedAt churn)";
  try {
    const s = freshStore();
    const firstChange = await s.apply(
      makeAttachmentCaptionPayload("att-1", "hi", 100),
      /* observedAt */ 1_000,
    );
    const firstObs = (await s.caption("att-1"))?.observedAt;
    const secondChange = await s.apply(
      makeAttachmentCaptionPayload("att-1", "hi", 100),
      /* observedAt */ 999_000,
    );
    const secondObs = (await s.caption("att-1"))?.observedAt;
    if (
      firstChange &&
      !secondChange &&
      firstObs === 1_000 &&
      secondObs === 1_000
    ) {
      return pass(name);
    }
    return fail(
      name,
      `firstChange=${firstChange} secondChange=${secondChange} firstObs=${firstObs} secondObs=${secondObs}`,
    );
  } catch (e) {
    return fail(name, String(e));
  }
}

async function testEmptyTextOverwritesExisting(): Promise<TestCase> {
  const name =
    "empty captionText overwrites existing (edit-to-clear model — record persists)";
  try {
    const s = freshStore();
    await s.apply(makeAttachmentCaptionPayload("att-1", "hello", 100));
    const changed = await s.apply(makeAttachmentCaptionPayload("att-1", "", 200));
    const r = await s.caption("att-1");
    // Record must STILL EXIST (explicit empty caption ≠ "no caption")
    if (changed && r !== undefined && r.captionText === "" && r.lastTs === 200) {
      return pass(name);
    }
    return fail(name, `changed=${changed} got=${JSON.stringify(r)}`);
  } catch (e) {
    return fail(name, String(e));
  }
}

async function testClearRemovesRecord(): Promise<TestCase> {
  const name = "clear(attachmentId) removes the record";
  try {
    const s = freshStore();
    await s.apply(makeAttachmentCaptionPayload("att-1", "hello", 100));
    await s.clear("att-1");
    const r = await s.caption("att-1");
    if (r === undefined) return pass(name);
    return fail(name, `expected undefined, got ${JSON.stringify(r)}`);
  } catch (e) {
    return fail(name, String(e));
  }
}

async function testResetAllEmpties(): Promise<TestCase> {
  const name = "resetAll() removes every caption record";
  try {
    const s = freshStore();
    await s.apply(makeAttachmentCaptionPayload("att-1", "a", 1));
    await s.apply(makeAttachmentCaptionPayload("att-2", "b", 1));
    await s.resetAll();
    const r1 = await s.caption("att-1");
    const r2 = await s.caption("att-2");
    if (r1 === undefined && r2 === undefined) return pass(name);
    return fail(name, `att-1=${JSON.stringify(r1)} att-2=${JSON.stringify(r2)}`);
  } catch (e) {
    return fail(name, String(e));
  }
}

async function testPersistenceAcrossInstances(): Promise<TestCase> {
  const name =
    "caption state persists across store instances (write → reopen DB → state preserved)";
  try {
    const dbName = `kaether:attachment-captions:selftest:persist:${Date.now()}`;
    const writer = new AttachmentCaptionStore(dbName);
    await writer.apply(makeAttachmentCaptionPayload("att-1", "persisted", 100));

    // Open a fresh instance pointing at the same DB name
    const reader = new AttachmentCaptionStore(dbName);
    const recovered = await reader.caption("att-1");
    if (recovered?.captionText === "persisted" && recovered.lastTs === 100) {
      return pass(name);
    }
    return fail(name, `recovered=${JSON.stringify(recovered)}`);
  } catch (e) {
    return fail(name, String(e));
  }
}

// ---------------------------------------------------------------------------
// C. setLocal convenience cases
// ---------------------------------------------------------------------------

async function testSetLocalRoundTripsViaCodec(): Promise<TestCase> {
  const name =
    "setLocal returns a valid payload that round-trips through the codec";
  try {
    const s = freshStore();
    const payload = await s.setLocal("att-1", "hello", 100);
    const encoded = AttachmentCaptionCodec.encode(payload);
    const decoded = AttachmentCaptionCodec.decode(encoded);
    if (
      decoded.attachmentId === "att-1" &&
      decoded.captionText === "hello" &&
      decoded.ts === 100 &&
      decoded.schema === payload.schema
    ) {
      return pass(name);
    }
    return fail(
      name,
      `got ${JSON.stringify(decoded)}, want ${JSON.stringify(payload)}`,
    );
  } catch (e) {
    return fail(name, String(e));
  }
}

async function testSetLocalOversizeThrows(): Promise<TestCase> {
  const name = "setLocal rejects oversize caption with captionTooLong (no store side-effect)";
  try {
    const s = freshStore();
    const oversize = "a".repeat(AttachmentCaptionCodec.MAX_UNICODE_SCALAR_COUNT + 1);
    try {
      await s.setLocal("att-1", oversize, 100);
      return fail(name, "setLocal did not throw");
    } catch (e) {
      if (
        e instanceof AttachmentCaptionError &&
        e.kind.tag === "captionTooLong" &&
        e.kind.unicodeScalarCount ===
          AttachmentCaptionCodec.MAX_UNICODE_SCALAR_COUNT + 1
      ) {
        // Verify no side-effect on the store
        const r = await s.caption("att-1");
        if (r === undefined) return pass(name);
        return fail(name, `store was mutated: ${JSON.stringify(r)}`);
      }
      return fail(name, `wrong error: ${e}`);
    }
  } catch (e) {
    return fail(name, String(e));
  }
}

async function testSetLocalEmptyAttachmentIdThrows(): Promise<TestCase> {
  const name = "setLocal rejects empty attachmentId with emptyAttachmentId";
  try {
    const s = freshStore();
    try {
      await s.setLocal("", "hi", 100);
      return fail(name, "setLocal did not throw");
    } catch (e) {
      if (
        e instanceof AttachmentCaptionError &&
        e.kind.tag === "emptyAttachmentId"
      ) {
        return pass(name);
      }
      return fail(name, `wrong error: ${e}`);
    }
  } catch (e) {
    return fail(name, String(e));
  }
}

async function testSetLocalUpdatesStoreSideEffect(): Promise<TestCase> {
  const name = "setLocal updates the store as a side effect";
  try {
    const s = freshStore();
    await s.setLocal("att-1", "side-effect", 100);
    const r = await s.caption("att-1");
    if (r?.captionText === "side-effect" && r.lastTs === 100) {
      return pass(name);
    }
    return fail(name, `got ${JSON.stringify(r)}`);
  } catch (e) {
    return fail(name, String(e));
  }
}

// ---------------------------------------------------------------------------
// D. Server-rebind resistance cases
// ---------------------------------------------------------------------------

async function testHostilePayloadRoutesByPayloadAttachmentId(): Promise<TestCase> {
  const name =
    "hostile payload routes to its own attachmentId, never the receiver's expected one";
  // The receiver "expected" a caption for "att-expected". A hostile
  // server-supplied payload names "att-different" inside its signed plaintext.
  // The store MUST route to that payload-named attachmentId — never to the
  // receiver's expected one — because the signed envelope binds the attachmentId.
  try {
    const s = freshStore();
    await s.apply(makeAttachmentCaptionPayload("att-expected", "real", 100));
    await s.apply(makeAttachmentCaptionPayload("att-different", "ATTACKER", 999));
    const real     = await s.caption("att-expected");
    const attacker = await s.caption("att-different");
    if (
      real?.captionText === "real" &&
      attacker?.captionText === "ATTACKER" &&
      attacker?.attachmentId === "att-different"
    ) {
      return pass(name);
    }
    return fail(
      name,
      `real=${JSON.stringify(real)} attacker=${JSON.stringify(attacker)}`,
    );
  } catch (e) {
    return fail(name, String(e));
  }
}

async function testForeignRatchetCaptionStillPassesCodec(): Promise<TestCase> {
  const name =
    "foreign-ratchet caption still passes codec (envelope authenticates, not codec)";
  // A caption from a different ratchet (foreign peer) is still a well-formed
  // AttachmentCaptionPayload at the codec layer. The codec only checks shape,
  // length, schema, and non-empty attachmentId. The signed envelope outside
  // this codec is what authenticates the sender. The trust boundary is the
  // envelope, not the codec.
  try {
    const p = makeAttachmentCaptionPayload("att-1", "injected", 1);
    const encoded = AttachmentCaptionCodec.encode(p);
    const decoded = AttachmentCaptionCodec.decode(encoded);
    if (
      decoded.attachmentId === p.attachmentId &&
      decoded.captionText === p.captionText &&
      decoded.ts === p.ts
    ) {
      return pass(name);
    }
    return fail(name, `decode mismatch: got ${JSON.stringify(decoded)}`);
  } catch (e) {
    return fail(name, String(e));
  }
}

async function testApplyHasNoExpectedAttachmentIdParam(): Promise<TestCase> {
  const name =
    "apply has no external expectedAttachmentId parameter — routing is payload-only";
  // Invariant: AttachmentCaptionStore.apply takes (payload, observedAt?)
  // and NOTHING ELSE. There is no "expectedAttachmentId" hatch through which a
  // caller could re-bind a payload to a different attachmentId on receive.
  // We assert by calling the exact signature and verifying routing comes
  // purely from payload.attachmentId.
  try {
    const s = freshStore();
    const p = makeAttachmentCaptionPayload("from-payload", "x", 1);
    await s.apply(p);
    await s.apply(p, Date.now()); // second call with optional observedAt — still just (payload, observedAt)
    const routedHere      = await s.caption("from-payload");
    const routedElsewhere = await s.caption("not-from-payload");
    if (routedHere?.captionText === "x" && routedElsewhere === undefined) {
      return pass(name);
    }
    return fail(
      name,
      `here=${JSON.stringify(routedHere)} elsewhere=${JSON.stringify(routedElsewhere)}`,
    );
  } catch (e) {
    return fail(name, String(e));
  }
}
